import ContractorDocuments from '../documents';
import { isEventThread } from 'src/content/applications/Messenger/eventThreadLinks';
import { formatDateOfBirth } from './dateOfBirth';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useWorkerProfile, legacyProfileResponse } from 'src/workspaces/workerProfile/WorkerProfileContext';
import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router-dom';
import {
  Star,
  Calendar,
  MapPin,
  Phone,
  Mail,
  User,
  Clock,
  DollarSign,
  Award,
  Briefcase,
  MessageCircle,
  FileText,
  TableIcon,
  MessageCircleIcon
} from 'lucide-react';
import { config } from 'src/config';
import {
  getAllPositionsCategories,
  getChatsByUserId,
  getUserById,
} from 'src/helper/backend_helper';
import UserAvatar from './UserAvatar';
import {
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import { Description } from '@mui/icons-material';
import { formatNumber, formatMoney } from 'src/utils/formatters';
import ActivityLogs from '../ActivityLogs';
import JobHistory from '../jobHistory';
import moment from 'moment';
import ContractorRatingSummary from 'src/content/applications/bilateralReviews/ContractorRatingSummary';

const getTimeDifference = (startTime: string, endTime: string) => {
  const [startHours, startMinutes] = startTime?.split(':').map(Number);
  const [endHours, endMinutes] = endTime?.split(':').map(Number);

  const startDate = new Date(0, 0, 0, startHours, startMinutes);
  const endDate = new Date(0, 0, 0, endHours, endMinutes);

  const diffMilliseconds = endDate.getTime() - startDate.getTime();
  const diffMinutes = Math.floor(diffMilliseconds / (1000 * 60));

  return diffMinutes;
};

const PersonalInformation = () => {
  const scope = useWorkerProfile();
  const location = useLocation();
  const userId = scope?.workerId ?? (location.state as any)?.user?.id;
  const [filter, setFilter] = useState('all');
  const [selectedPositions, setSelectedPositions] = useState<number[]>([]);
  const [categoriesWithPositions, setCategoriesWithPositions] = useState<any[]>(
    []
  );
  const [lateCounts, setLateCounts] = useState({
    lessThan15: 0,
    moreThan15: 0,
    ncns: 0
  });


  const { data, error, isLoading, isError }: any = useQuery(
    ['user', scope?.companyId, scope?.revision, userId], // Unique key for caching
    () => scope ? legacyProfileResponse(scope.data.user) : getUserById(userId), // Fetch function
    {
      onSuccess: (data: any) => {
        setSelectedPositions(JSON.parse(data?.data?.data?.experiences || '[]'));
      },
      keepPreviousData: true // Keeps previous data while fetching new data
    }
  );

  const user = data?.data?.data;
  const openScopedTaxDocument = async (event: React.MouseEvent, type: string) => {
    if (!scope) return;
    event.preventDefault();
    const document = scope.data.documents.find((item) => item.type === type);
    if (!document) return;
    const preview = window.open('', '_blank');
    if (!preview) { toast.error('Allow pop-ups to view the document.'); return; }
    preview.opener = null;
    try {
      const response = await axios.get(`${scope.basePath}/documents/${document.id}/file`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      preview.location.href = url;
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      preview.close();
      toast.error('Document is unavailable in this company.');
    }
  };


  function getTimeDifference(startTime, clockIn) {
    const [startHours, startMinutes] = startTime?.split(':').map(Number);
    const clockInTime = moment(clockIn).format('HH:mm');
    const [clockHours, clockMinutes] = clockInTime.split(':').map(Number);

    return clockHours * 60 + clockMinutes - (startHours * 60 + startMinutes);
  }

  const totalLateAbove15 = user?.jobs?.filter((job) => {
    if (!job.clockIn) return false;
    const diff = getTimeDifference(job.startTime, job.clockIn);
    return diff > 15;
  }).length;



  // Fetch position categories and positions
  useEffect(() => {
    const fetchCategoriesWithPositions = async () => {
      const response = scope ? await legacyProfileResponse(scope.data.categories) : await getAllPositionsCategories();
      setCategoriesWithPositions(response?.data?.data || []);
    };

    fetchCategoriesWithPositions();
  }, [scope]);

  const {
    data: chatLogs = [],
    isPending: chatLogsLoading,
    isError: chatLogsError
  }: any = useQuery({
    queryKey: ['user_chat_logs', scope?.companyId, scope?.revision, userId],
    queryFn: () => scope ? legacyProfileResponse(scope.data.chats) : getChatsByUserId(userId),
    select: (response) => {

      return response?.data?.data || [];
    },
    enabled: !!userId
  });

  if (isLoading) {
    return (
      <div className="bg-gradient-secondary p-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-card rounded-xl p-6 shadow-soft">
                {/* Title shimmer */}
                <div className="h-6 rounded mb-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-[shimmer_2s_infinite]" />

                {/* Lines shimmer */}
                <div className="space-y-3">
                  {[...Array(4)].map((_, j) => (
                    <div
                      key={j}
                      className="h-4 rounded w-3/4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-[shimmer_2s_infinite]"
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!user) return null;

  // Filter categories to only show selected positions
  const filteredCategories = categoriesWithPositions
    .map((category) => ({
      ...category,
      positions:
        category.positions?.filter((position: any) =>
          selectedPositions.includes(position.id)
        ) || []
    }))
    .filter((category) => category.positions.length > 0);

  const StarRating = ({
    rating,
    size = 16
  }: {
    rating: number;
    size?: number;
  }) => {
    return (
      // <div className="flex items-center gap-1">
      //   {[...Array(5)].map((_, i) => (
      //     <Star
      //       key={i}
      //       size={size}
      //       fill='currentColor'
      //       className={`${
      //         i < Math.floor(rating)
      //           ? 'fill-gold text-yellow-600'
      //           : i < rating
      //           ? 'fill-gold/50 text-gold'
      //           : 'text-muted'
      //       }`}
      //     />
      //   ))}
      //   <span className="text-sm text-muted-foreground ml-1">
      //     {rating.toFixed(1)}
      //   </span>
      // </div>
      <div className="flex items-center gap-1">
        {[...Array(5)].map((_, i) => {
          const isFull = i < Math.floor(rating);
          const isHalf = i < rating && !isFull;

          return (
            <Star
              key={i}
              size={size}
              // Apply fill ONLY when needed
              fill={isFull || isHalf ? 'currentColor' : 'none'}
              strokeWidth={isFull || isHalf ? 0 : 1.5}
              className={
                isFull
                  ? 'text-yellow-600' // full star
                  : isHalf
                  ? 'text-yellow-300' // half star look
                  : 'text-gray-300' // empty star (gray outline)
              }
            />
          );
        })}

        <span className="text-sm text-muted-foreground ml-1">
          {rating.toFixed(1)}
        </span>
      </div>
    );
  };

  return (
    <div className="bg-background">
      {/* Header with gradient */}

      {/* <div className="bg-gradient-to-br from-[#E8BF21] to-[#000000] px-8 py-8">
        <div className="max-w-7xl  flex items-center gap-6">
          <div className="relative">
            <UserAvatar
              id={user.id}
              image={config.FILe_URL + user.profileImage}
              width={80}
              height={80}
              hideName
              isFavorite={user?.isFavorite}
            />
          </div>
          <div className="text-white">
            <h1 className="text-3xl font-bold mb-2">
              {user.firstName} {user.lastName}
            </h1>
            <div className="flex items-center gap-4 text-white/90">
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <span className="text-sm">
                  Joined {new Date(user.createdAt).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div> */}

      <div className="worker-profile-identity relative bg-gradient-to-br from-[#E8BF21]  to-[#000000] px-6 py-6 overflow-hidden rounded-xl">
        {/* Subtle gradient orbs */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/2 w-56 h-56 bg-[#FFD700]/20 blur-[90px] rounded-full -translate-x-1/2"></div>
          <div className="absolute bottom-0 right-1/4 w-48 h-48 bg-white/10 blur-[80px] rounded-full"></div>
        </div>

        <div className="relative max-w-7xl mx-auto flex flex-col lg:flex-row lg:items-center gap-6 text-white z-10">
          {/* Avatar + Name */}
          <div className="flex items-center gap-4">
            <div className="relative group">
              <div className="absolute -inset-0.5 rounded-full bg-gradient-to-r from-[#FFD700] to-[#E8BF21] blur-sm opacity-80 group-hover:opacity-100 transition duration-300"></div>
              <UserAvatar
                id={user.id}
                image={config.FILe_URL + user.profileImage}
                width={80}
                height={80}
                hideName
                isFavorite={user?.isFavorite}
              />
            </div>

            <div>
              <h1 className="text-2xl font-bold leading-tight">
                {user.firstName} {user.lastName}
              </h1>

              <div className="flex items-center gap-1 text-sm text-white/80 mt-1">
                <Calendar className="w-4 h-4" />
                <span>
                  Joined {new Date(user.createdAt).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="hidden lg:block w-px h-16 bg-white/20 mx-6"></div>

          {/* Personal Info (Glass card compact) */}
          <div className="flex-1 flex flex-col gap-4">
            <div className="backdrop-blur-md bg-white/10 rounded-xl border border-white/10 p-4 shadow-sm grid sm:grid-cols-3 gap-x-6 gap-y-3 text-sm">
              {/* Email */}
              <div title="Email" className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#FFD700]" />
                <span className="truncate">{user.email}</span>
              </div>

              {/* Phone */}
              <div title="Phone Number" className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-[#FFD700]" />
                <span>{user.phoneNumber}</span>
              </div>

              {/* DOB */}
              <div title="Date of Birth" className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#FFD700]" />
                <span>{formatDateOfBirth(user.dob)}</span>
              </div>

              {/* Address */}
              <div
                title="Address"
                className="flex items-center gap-2 col-span-2 sm:col-span-1 lg:col-span-2"
              >
                <MapPin className="w-4 h-4 text-[#FFD700]" />
                <span className="truncate">
                  {user.address}, {user.city}, {user.state} {user.zipcode}
                </span>
              </div>

              <div title="Primary experience">
                <p className="text-white/80">Primary experience</p>
                <p className="font-medium">
                  {user.primaryExperienceCategory || 'Not recorded'}
                </p>
              </div>
              <div title="Secondary experience">
                <p className="text-white/80">Secondary experience</p>
                <p className="font-medium">
                  {user.secondaryExperienceCategory || 'None'}
                </p>
              </div>

              {/* I9 Form */}
              {/* <div title="I9 Form" className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#FFD700]" />
                <a
                  href={scope ? '#' : config.FILe_URL + user.i9Form}
                      onClick={(event) => void openScopedTaxDocument(event, 'I9')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-2 py-0.5 bg-gradient-to-r from-[#E8BF21] to-[#b89615] rounded text-xs font-medium text-black hover:brightness-110 transition"
                >
                  View I9 Form
                </a>
              </div> */}
              <div className="flex items-center gap-2">
                {(scope ? scope.data.documents.some((doc) => doc.type === 'I9') : Boolean(user.i9Form)) && (
                  <div title="I9 Form" className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[#FFD700]" />
                    <a
                      href={scope ? '#' : config.FILe_URL + user.i9Form}
                      onClick={(event) => void openScopedTaxDocument(event, 'I9')}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-2 py-0.5 bg-gradient-to-r from-[#E8BF21] to-[#b89615] rounded text-xs font-medium text-black hover:brightness-110 transition"
                    >
                      View I9 Form
                    </a>
                  </div>
                )}

                {(scope ? scope.data.documents.some((doc) => doc.type === 'W9') : Boolean(user.w9Form)) && (
                  <div title="W9 Form" className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[#FFD700]" />
                    <a
                      href={scope ? '#' : config.FILe_URL + user.w9Form}
                      onClick={(event) => void openScopedTaxDocument(event, 'W9')}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-2 py-0.5 bg-gradient-to-r from-[#E8BF21] to-[#b89615] rounded text-xs font-medium text-black hover:brightness-110 transition"
                    >
                      View W9 Form
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Link
              to="/dashboards/messenger"
              state={{ id: userId }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#E8BF21] to-[#b89615] rounded-lg text-black hover:brightness-110 transition shadow-lg"
            >
              <MessageCircle className="w-4 h-4" />
              <span className="text-sm font-medium">Chat</span>
            </Link>
          </div>
        </div>
      </div>

      {scope && <ContractorDocuments reviewOnly />}
      <div className="worker-profile-content max-w-full px-8 py-8">
        <div className="grid grid-cols-1 gap-6">
          {userId ? <ContractorRatingSummary userId={userId} /> : null}
          {/* Personal Information */}
          {/* <div className="bg-card rounded-xl p-6 shadow-soft border border-border/50">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-8 h-8 bg-gradient-primary rounded-lg flex items-center justify-center">
                <User className="w-4 h-4" />
              </div>
              <h2 className="text-xl font-semibold text-foreground">
                Personal Information
              </h2>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{user.email}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Phone</p>
                  <p className="font-medium">{user.phoneNumber}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Date of Birth</p>
                  <p className="font-medium">
                    {formatDateOfBirth(user.dob)}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <MapPin className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Address</p>
                  <p className="font-medium">
                    {user.address}
                    <br />
                    {user.city}, {user.state} {user.zipcode}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <FileText className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">I9 Form</p>
                  <p className="font-medium">
                    <a
                      href={scope ? '#' : config.FILe_URL + user.i9Form}
                      onClick={(event) => void openScopedTaxDocument(event, 'I9')}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="border rounded-lg mt-2 inline-flex items-center px-3 py-1 text-sm font-medium text-gold hover:text-gold/80 transition-colors"
                    >
                      View
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div> */}

          {/* Work History */}
          {/* <div className="bg-card rounded-xl p-6 shadow-soft border border-border/50">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-8 h-8 bg-gradient-primary rounded-lg flex items-center justify-center">
                <Briefcase className="w-4 h-4" />
              </div>
              <h2 className="text-xl font-semibold text-foreground">
                Work History
              </h2>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gold-light/30 rounded-lg p-3">
                  <p className="text-2xl font-bold text-gold">
                    {user.jobCount || 0}
                  </p>
                  <p className="text-sm text-muted-foreground">Shifts Worked</p>
                </div>
                <div className="bg-gold-light/20 rounded-lg px-2 py-2">
                  <div className="text-3xl font-bold text-gold mb-1">
                    {user.jobCount > 0
                      ? (user.totalRating / user.jobCount).toFixed(1)
                      : '0.0'}
                  </div>
                  <StarRating
                    rating={
                      user.jobCount > 0 ? user.totalRating / user.jobCount : 0
                    }
                    size={20}
                  />
                </div>
              </div>

                  <JobHistory />
            </div>
          </div> */}

          <div className="bg-card rounded-xl p-6 shadow-soft border border-border/50">
            {/* header row */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
              {/* title */}
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="relative w-9 h-9 rounded-xl flex items-center justify-center shadow-sm">
                    <Briefcase className="w-4 h-4 text-black" />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-semibold text-foreground tracking-tight">
                    All Shifts
                  </h2>
                </div>
              </div>

              {/* stat pills */}
              <div className="flex items-center gap-3">
                <div
                  onClick={() =>
                    setFilter((current) =>
                      current === 'completed' ? 'all' : 'completed'
                    )
                  }
                  className={`group relative rounded-lg px-3 py-2 border border-border/60 backdrop-blur-md shadow-[0_1px_0_#ffffff66_inset] hover:shadow transition-all
    ${
      filter === 'completed'
        ? 'bg-gradient-to-b from-[#E8BF21] to-[#b89615] text-black border-transparent'
        : 'bg-gradient-to-b from-white/70 to-white/40 dark:from-white/10 dark:to-white/[0.04]'
    }
  `}
                >
                  <div
                    className={`absolute -inset-[1px] cursor-pointer rounded-lg bg-gradient-to-r from-[#E8BF21] via-transparent to-[#E8BF21]/40 blur-[2px] transition
      ${
        filter === 'completed'
          ? 'opacity-60'
          : 'opacity-0 group-hover:opacity-60'
      }
    `}
                  />
                  <div className="relative flex items-center gap-2">
                    <div
                      className={`h-6 w-6 rounded-md flex items-center justify-center text-xs font-bold shadow
        ${
          filter === 'completed'
            ? 'bg-gradient-to-br from-[#b89615] to-[#E8BF21] text-black'
            : 'bg-gradient-to-br from-[#E8BF21] to-[#b89615] text-black'
        }
      `}
                    >
                      {user.completedShiftCount ?? 0}
                    </div>
                    <div className="leading-tight">
                      <div
                        className={`text-xs ${
                          filter === 'completed'
                            ? 'text-black'
                            : 'text-muted-foreground'
                        }`}
                      >
                        Completed Shifts
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className="group relative rounded-lg px-3 py-2 border border-border/60 bg-gradient-to-b from-white/70 to-white/40 dark:from-white/10 dark:to-white/[0.04] backdrop-blur-md shadow-[0_1px_0_#ffffff66_inset]
                   hover:shadow transition-all"
                >
                  <div className="absolute -inset-[1px] rounded-lg bg-gradient-to-r from-[#E8BF21] via-transparent to-[#E8BF21]/40 opacity-0 group-hover:opacity-60 blur-[2px] transition" />
                  <div className="relative flex items-center gap-2">
                    <div className="h-6 w-6 rounded-md bg-gradient-to-br from-[#E8BF21] to-[#b89615] text-black flex items-center justify-center text-xs font-bold shadow">
                      {user.jobCount || 0}
                    </div>
                    <div className="leading-tight">
                      <div className="text-xs text-muted-foreground">
                        Shifts Worked
                      </div>
                    </div>
                  </div>
                </div>

                {/* <div
                  className="group relative rounded-lg px-3 py-2 border border-border/60 bg-gradient-to-b from-white/70 to-white/40 dark:from-white/10 dark:to-white/[0.04] backdrop-blur-md shadow-[0_1px_0_#ffffff66_inset]
                   hover:shadow transition-all"
                >
                  <div className="absolute -inset-[1px] rounded-lg bg-gradient-to-r from-[#E8BF21] via-transparent to-[#E8BF21]/40 opacity-0 group-hover:opacity-60 blur-[2px] transition" />
                  <div className="relative flex items-center gap-2">
                    <div className="h-6 w-24 rounded-md bg-gradient-to-br from-[#E8BF21] to-[#b89615] text-black flex items-center justify-center text-xs font-bold shadow">
                      {formatMoney(user.totalPaidContractor)}
                    </div>
                    <div className="leading-tight">
                      <div className="text-xs text-muted-foreground">
                        Total Paid
                      </div>
                    </div>
                  </div>
                </div> */}

                <div
                  className="group relative rounded-lg px-3 py-2 border border-border/60 bg-gradient-to-b from-white/70 to-white/40 dark:from-white/10 dark:to-white/[0.04] backdrop-blur-md shadow-[0_1px_0_#ffffff66_inset]
                   hover:shadow transition-all"
                >
                  <div className="absolute -inset-[1px] rounded-lg bg-gradient-to-r from-[#E8BF21] via-transparent to-[#E8BF21]/40 opacity-0 group-hover:opacity-60 blur-[2px] transition" />
                  <div className="relative flex items-center gap-2">
                    <div className="h-6 w-6 rounded-md bg-gradient-to-br from-[#E8BF21] to-[#b89615] text-black flex items-center justify-center text-xs font-bold shadow">
                      {/* {user.TotalNoCallNoShow || 0} */}0
                    </div>
                    <div className="leading-tight">
                      <div className="text-xs text-muted-foreground">
                        No Call No Shows
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className="group relative rounded-lg px-3 py-2 border border-border/60 bg-gradient-to-b from-white/70 to-white/40 dark:from-white/10 dark:to-white/[0.04] backdrop-blur-md shadow-[0_1px_0_#ffffff66_inset]
                   hover:shadow transition-all"
                >
                  <div className="absolute -inset-[1px] rounded-lg bg-gradient-to-r from-[#E8BF21] via-transparent to-[#E8BF21]/40 opacity-0 group-hover:opacity-60 blur-[2px] transition" />
                  <div className="relative flex items-center gap-2">
                    <div className="h-6 w-6 rounded-md bg-gradient-to-br from-[#E8BF21] to-[#b89615] text-black flex items-center justify-center text-xs font-bold shadow">
                      {totalLateAbove15 || 0}
                    </div>
                    <div className="leading-tight">
                      <div className="text-xs text-muted-foreground">
                        Late Shifts
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className="group relative rounded-lg px-3 py-2 border border-border/60 bg-gradient-to-b from-white/70 to-white/40 dark:from-white/10 dark:to-white/[0.04] backdrop-blur-md shadow-[0_1px_0_#ffffff66_inset]
                   hover:shadow transition-all"
                >
                  <div className="absolute -inset-[1px] rounded-lg bg-gradient-to-r from-[#E8BF21] via-transparent to-[#E8BF21]/40 opacity-0 group-hover:opacity-60 blur-[2px] transition" />
                  <div className="relative flex items-center gap-2">
                    <div className="h-6 w-6 rounded-md bg-gradient-to-br from-[#E8BF21] to-[#b89615] text-black flex items-center justify-center text-xs font-bold shadow">
                      {user.TotalLastMinuteCancellation || 0}
                    </div>
                    <div className="leading-tight">
                      <div className="text-xs text-muted-foreground">
                        Last Minute Cancellations
                      </div>
                    </div>
                  </div>
                </div>

                {/* rating */}
                <div
                  className="group relative rounded-lg px-3 py-2 border border-border/60 bg-gradient-to-b from-white/70 to-white/40 dark:from-white/10 dark:to-white/[0.04] backdrop-blur-md shadow-[0_1px_0_#ffffff66_inset]
                   hover:shadow transition-all"
                >
                  <div className="absolute -inset-[1px] rounded-lg bg-gradient-to-r from-[#FFD700]/70 via-transparent to-[#E8BF21]/60 opacity-0 group-hover:opacity-70 blur-[2px] transition" />
                  <div className="relative flex items-center gap-3">
                    <div className="flex flex-col">
                      <div className="mt-1 flex items-center">
                        {user.ratedJobCount > 0 ? (
                          <StarRating
                            rating={user.totalProfessionalReview / user.ratedJobCount}
                            size={16}
                          />
                        ) : (
                          <span className="text-sm text-muted-foreground">No Rating Yet</span>
                        )}
                        <span className="text-sm text-muted-foreground ml-1">
                          ({user.completedShiftCount ?? 0} Completed Shifts)
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* content */}
            <div>
              <JobHistory filter={filter} />
            </div>
          </div>

          {/* Ratings */}
          {/* <div className="bg-card rounded-xl p-6 shadow-soft border border-border/50">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-8 h-8 bg-gradient-primary rounded-lg flex items-center justify-center">
                <Award className="w-4 h-4 " />
              </div>
              <h2 className="text-xl font-semibold text-foreground">
                Financial History
              </h2>
            </div>

      
            <div className="overflow-x-auto overflow-y-auto max-h-[400px] ">
              <table className="min-w-full text-sm border border-border/40 rounded-lg overflow-hidden">
                <thead className="bg-muted/30">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-foreground/80 whitespace-nowrap">
                      Event Title
                    </th>
              
                    <th className="px-4 py-3 text-left font-semibold text-foreground/80 whitespace-nowrap">
                      Invoice Status
                    </th>
                    <th className="px-4 py-3 text-left font-semibold text-foreground/80 whitespace-nowrap">
                      Invoice Total
                    </th>
                    <th className="px-4 py-3 text-left font-semibold text-foreground/80 whitespace-nowrap">
                      Payment Status
                    </th>
                    <th className="px-4 py-3 text-left font-semibold text-foreground/80 whitespace-nowrap">
                      Payout Method
                    </th>
                    <th className="px-4 py-3 text-left font-semibold text-foreground/80 whitespace-nowrap">
                      Date Paid
                    </th>
                    <th className="px-4 py-3 text-left font-semibold text-foreground/80 whitespace-nowrap">
                      Total Paid
                    </th>
                    <th className="px-4 py-3 text-left font-semibold text-foreground/80 whitespace-nowrap">
                      Instant Pay
                    </th>
                    <th className="px-4 py-3 text-left font-semibold text-foreground/80 whitespace-nowrap">
                      Invoice
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {financialsHistoryLoading ? (
                    <CircularProgress size={18} />
                  ) : financialsHistory?.length > 0 ? (
                    financialsHistory.map((item: any) => (
                      <tr
                        key={item.id}
                        className="border-t border-border/30 hover:bg-muted/10 transition"
                      >
                        <td className="px-4 py-3 nowrap">
                          <Link
                            to={`/dashboards/jobTracker/positions/${item.Event?.id}`}
                            state={{ title: item.Event?.title }}
                          >
                            <span
                              style={{
                                color: 'blue',
                                textDecoration: 'underline'
                              }}
                            >
                              {item.Event?.title}
                            </span>
                          </Link>
                        </td>
                      
                        <td className="px-4 py-3">
                          {item.contractorInvoiceStatus}
                        </td>
                        <td className="px-4 py-3">
                          {formatMoney(item.contractorInvoiceTotal)}
                        </td>

                        <td className="px-4 py-3">
                          {item.shiftPaymentStatus || '—'}
                        </td>
                        <td className="px-4 py-3">
                          {item.payoutMethod || '—'}
                        </td>
                        <td className="px-4 py-3">
                          {item.datePaid
                            ? new Date(item.datePaid).toLocaleDateString()
                            : '—'}
                        </td>
                        <td className="px-4 py-3 font-semibold text-green-600">
                          {formatMoney(item.totalPaidContractor)}
                        </td>

                        <td className="px-4 py-3 font-semibold">
                          {item?.instantPay ? (
                            <input type="checkbox" checked={item?.instantPay} />
                          ) : (
                            <input type="checkbox" disabled checked={false} />
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <IconButton
                            size="small"
                            color="primary"
                            component="a"
                            href={config.FILe_URL + item?.contractorInvoice}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Tooltip title="View Invoice" arrow>
                              <Description fontSize="small" />
                            </Tooltip>
                          </IconButton>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-4 py-6 text-center text-muted-foreground"
                      >
                        No paid financial history found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="bg-green-50 rounded-lg p-3 flex justify-between items-center mt-1">
              <p className="text-lg text-muted-foreground">Total Paid</p>
              <p className="text-2xl font-bold text-green-600">
                {formatMoney(
                  financialsHistory.reduce(
                    (sum: number, item: any) =>
                      sum + (item.totalPaidContractor || 0),
                    0
                  )
                )}
              </p>
            </div>
          </div> */}
        </div>

        {/* Skills & Competencies */}

        {/* <div className="mt-6 bg-card rounded-xl p-6 shadow-soft border border-border/50">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-8 h-8 bg-gradient-primary rounded-lg flex items-center justify-center">
              <Award className="w-4 h-4" />
            </div>
            <h2 className="text-xl font-semibold text-foreground">
              Skills & Competencies
            </h2>
          </div>

          <div className="space-y-6">
            {filteredCategories.map((category) => (
              <div key={category.id}>
                <h3 className="font-semibold text-foreground mb-3">
                  {category.name}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {category.positions.map((position: any) => (
                    <span
                      key={position.id}
                      className="inline-flex items-center px-3 py-1 rounded-md text-sm font-medium bg-gradient-to-br from-[#E8BF21] to-[#000000] text-white shadow-sm"
                    >
                      {position.name}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div> */}

        {/* <div className="mt-6 bg-card rounded-xl p-6 shadow-soft border border-border/50">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-primary rounded-lg flex items-center justify-center">
                <Award className="w-4 h-4" />
              </div>
              <h2 className="text-xl font-semibold text-foreground tracking-tight">
                Skills & Competencies
              </h2>
            </div>
          </div> */}

        {/* <div className="space-y-6">
            {filteredCategories.map((category) => (
              <div key={category.id}>
                <h3 className="font-semibold text-foreground mb-3 text-base border-l-4 border-[#E8BF21] pl-2">
                  {category.name}
                </h3>

                <div className="flex flex-wrap gap-2">
                  {category.positions.map((position: any) => (
                    <span
                      key={position.id}
                      className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium 
                        bg-gradient-to-br from-[#E8BF21] via-[#c9a415] to-[#000000] text-white 
                        shadow-[0_2px_6px_rgba(0,0,0,0.2)] transition-all hover:scale-105 hover:shadow-lg"
                    >
                      <span className="mr-1.5 w-2 h-2 bg-white rounded-full opacity-80"></span>
                      {position.name}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div> */}
        {/* </div> */}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left: Skills & Competencies */}
          <div className="lg:col-span-6 mt-6 bg-card rounded-xl p-6 shadow-soft border border-border/50">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-primary rounded-lg flex items-center justify-center">
                  <Award className="w-4 h-4" />
                </div>
                <h2 className="text-xl font-semibold text-foreground tracking-tight">
                  Skills &amp; Competencies
                </h2>
              </div>
            </div>

            {/* Scrollable content with reduced height */}
            <div
              className=" space-y-6 overflow-y-auto pr-2"
              style={{ maxHeight: 420 }}
            >
              {filteredCategories.map((category) => (
                <div key={category.id}>
                  <h3 className="font-semibold text-foreground mb-3 text-base border-l-4 border-[#E8BF21] pl-2">
                    {category.name}
                  </h3>

                  <div className="flex flex-wrap gap-2">
                    {category.positions.map((position) => (
                      <span
                        key={position.id}
                        className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium 
                    bg-gradient-to-br from-[#E8BF21] via-[#c9a415] to-[#000000] text-white 
                    shadow-[0_2px_6px_rgba(0,0,0,0.2)] transition-all hover:scale-105 hover:shadow-lg"
                      >
                        <span className="mr-1.5 w-2 h-2 bg-white rounded-full opacity-80"></span>
                        {position.name}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Chat Logs (MUI Table) */}
          <div className="lg:col-span-6 mt-6 bg-card rounded-xl p-6 shadow-soft border border-border/50">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-primary rounded-lg flex items-center justify-center">
                  <MessageCircleIcon className="w-4 h-4" />
                </div>
                <h2 className="text-xl font-semibold text-foreground tracking-tight">
                  Chat Logs
                </h2>
              </div>
            </div>

            {/* MUI Table: sticky header + scroll */}
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                maxHeight: 420,
                borderRadius: '12px'
                // border: "1px solid",
                // borderColor: "divider",
              }}
            >
              <Table stickyHeader size="small" aria-label="skills table">
                <TableHead>
                  <TableRow>
                    <TableCell>Job Number</TableCell>
                    <TableCell>Position</TableCell>
                    <TableCell>Group Name</TableCell>
                    <TableCell>Total Participants</TableCell>
                    <TableCell align="center">Chat Logs</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {chatLogsLoading ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        <CircularProgress size={18} />
                      </TableCell>
                    </TableRow>
                  ) : (
                    chatLogs?.map((row) => {
                      // #4005: worker-event threads are archived here by event.
                      const event = row?.shift?.Event ?? row?.Event;
                      const eventThread = isEventThread(row);
                      return (
                      <TableRow key={row.id}>
                        <TableCell>
                          <Link
                            to={`/dashboards/jobTracker/positions/${event?.id}`}
                            state={{ title: event?.title }}
                          >
                            <span
                              style={{
                                color: 'blue',
                                textDecoration: 'underline'
                              }}
                            >
                              {event?.title}
                            </span>
                          </Link>
                        </TableCell>
                        <TableCell>{eventThread ? 'Event Thread' : row?.shift?.position}</TableCell>
                        <TableCell>{row?.groupName}</TableCell>
                        <TableCell>{row?.participants?.length}</TableCell>

                        <TableCell>
                          <Link
                            to={eventThread ? `/dashboards/messenger?tab=events&chatId=${row?.id}` : '/dashboards/group-chat-messages'}
                            state={eventThread ? undefined : { chatId: row?.id, userId: userId }}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#E8BF21] to-[#b89615] rounded-lg text-black hover:brightness-110 transition shadow-lg"
                          >
                            <MessageCircle className="w-4 h-4" />
                            <span className="text-sm font-medium">
                              View Chats
                            </span>
                          </Link>
                        </TableCell>
                      </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalInformation;

// function getTimeDifference(startTime, endTime) {
//   const [startHours, startMinutes] = startTime?.split(':').map(Number);
//   const [endHours, endMinutes] = endTime?.split(':').map(Number);

//   const startDate: any = new Date(0, 0, 0, startHours, startMinutes);
//   const endDate: any = new Date(0, 0, 0, endHours, endMinutes);

//   const diffMilliseconds = endDate - startDate;
//   const diffMinutes = Math.floor(diffMilliseconds / (1000 * 60));

//   return diffMinutes;
// }
